'use client'

import { useState, useRef, useEffect, useCallback } from 'react'
import { useRouter } from 'next/navigation'
import { Search } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { StepIndicator } from './StepIndicator'
import { AgentCard } from './AgentCard'
import { StepActions, StepError, StepPrompt } from './StepParts'

interface AddressSuggestion {
  sla: string
  score: number
  pid: string
}

interface AppraisalStep1Props {
  agentContext?: { slug: string; name: string; photoUrl: string | null } | null
}

/**
 * Appraisal step 1 of 5 — Figma 4096:93933 (desktop) / 4096:94790 (tablet) /
 * 4096:95789 (mobile). `Search` set: prompt (Body/bodyMdAlt) + gap/gap-6 24 +
 * a full-column Trigger, then gap/gap-8 32 to the "Start" button.
 * The H1 lives in the full-width Hero band (see AppraisalLayout).
 */
export function AppraisalStep1({ agentContext }: AppraisalStep1Props) {
  const router = useRouter()
  const [address, setAddress] = useState('')
  const [suggestions, setSuggestions] = useState<AddressSuggestion[]>([])
  const [isOpen, setIsOpen] = useState(false)
  const [highlightIndex, setHighlightIndex] = useState(-1)
  const [error, setError] = useState('')
  const wrapperRef = useRef<HTMLDivElement>(null)
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setIsOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClick)
    return () => document.removeEventListener('mousedown', handleClick)
  }, [])

  const fetchSuggestions = useCallback((query: string) => {
    if (debounceRef.current) clearTimeout(debounceRef.current)

    if (query.length < 3) {
      setSuggestions([])
      setIsOpen(false)
      return
    }

    debounceRef.current = setTimeout(async () => {
      try {
        const res = await fetch(`/api/address-lookup?q=${encodeURIComponent(query)}`)
        const data: AddressSuggestion[] = await res.json()
        setSuggestions(data.slice(0, 8))
        setIsOpen(data.length > 0)
        setHighlightIndex(-1)
      } catch {
        setSuggestions([])
        setIsOpen(false)
      }
    }, 250)
  }, [])

  const handleSelect = (sla: string) => {
    setAddress(sla)
    setSuggestions([])
    setIsOpen(false)
    setError('')
  }

  const handleSubmit = () => {
    if (!address.trim()) {
      setError('Please enter your property address.')
      return
    }
    const params = new URLSearchParams()
    params.set('address', address)
    if (agentContext?.slug) params.set('agent', agentContext.slug)
    router.push(`/appraisal/step-2?${params.toString()}`)
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault()
      setHighlightIndex((prev) => Math.min(prev + 1, suggestions.length - 1))
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      setHighlightIndex((prev) => Math.max(prev - 1, -1))
    } else if (e.key === 'Enter') {
      e.preventDefault()
      if (highlightIndex >= 0 && suggestions[highlightIndex]) {
        handleSelect(suggestions[highlightIndex].sla)
      } else {
        handleSubmit()
      }
    } else if (e.key === 'Escape') {
      setIsOpen(false)
    }
  }

  return (
    <div className="flex flex-col items-start gap-8">
      <StepIndicator currentStep={1} totalSteps={4} />

      {agentContext && <AgentCard name={agentContext.name} photoUrl={agentContext.photoUrl} />}

      <div className="w-full">
        <StepPrompt>What is the address you would like appraised?</StepPrompt>

        <div ref={wrapperRef} className="relative">
          <Input
            type="text"
            value={address}
            onChange={(e) => {
              setAddress(e.target.value)
              setError('')
              fetchSuggestions(e.target.value)
            }}
            onKeyDown={handleKeyDown}
            onFocus={() => {
              if (suggestions.length > 0) setIsOpen(true)
            }}
            placeholder="Search for your address"
            autoComplete="off"
            className="pr-10"
          />
          <Search
            aria-hidden="true"
            className="pointer-events-none absolute right-3 top-1/2 size-4 -translate-y-1/2 text-foreground"
          />
          {isOpen && suggestions.length > 0 && (
            // Figma `menu item` 2198:36854 — theme/background surface, radius sm,
            // padding p-2/p-1.5, Body/bodySm; highlighted row = theme/secondary.
            <ul className="absolute z-50 top-full left-0 right-0 mt-1 max-h-64 overflow-y-auto rounded-sm border border-border bg-background shadow-xs">
              {suggestions.map((suggestion, i) => (
                <li
                  key={suggestion.pid}
                  onMouseDown={(e) => {
                    e.preventDefault()
                    handleSelect(suggestion.sla)
                  }}
                  onMouseEnter={() => setHighlightIndex(i)}
                  className={`cursor-pointer px-2 py-1.5 text-left text-body-sm font-light text-foreground ${
                    i === highlightIndex ? 'bg-secondary' : 'bg-background'
                  }`}
                >
                  {suggestion.sla}
                </li>
              ))}
            </ul>
          )}
        </div>

        {error && <StepError>{error}</StepError>}

        <StepActions>
          <Button onClick={handleSubmit}>Start</Button>
        </StepActions>
      </div>
    </div>
  )
}
